/**
 * Pre-chaves do X3DH.
 *
 * Na instalacao o aparelho publica 1 Signed PreKey (rotacionada a cada 7
 * dias) e 100 One-Time PreKeys. Publicar so material publico e o que permite
 * a Alice iniciar conversa com o Bob offline sem o servidor saber de nada.
 */
import type { Identity } from './identity.js'
import { toBase64Url, wipe } from './bytes.js'
import { sodium } from './sodium.js'

export const SIGNED_PREKEY_LIFETIME_SECONDS = 7 * 24 * 60 * 60
export const DEFAULT_ONE_TIME_PREKEY_COUNT = 100

export interface SignedPreKeyPublic {
  readonly keyId: number
  readonly publicKey: Uint8Array
  /** Assinatura Ed25519 sobre `publicKey`, feita pela identidade do dono. */
  readonly signature: Uint8Array
  readonly createdAt: number
}

export interface SignedPreKey extends SignedPreKeyPublic {
  readonly privateKey: Uint8Array
}

export interface OneTimePreKeyPublic {
  readonly keyId: number
  readonly publicKey: Uint8Array
}

export interface OneTimePreKey extends OneTimePreKeyPublic {
  readonly privateKey: Uint8Array
}

/** O que o Worker devolve em `GET /v1/prekeys/:fingerprint`. */
export interface PreKeyBundle {
  readonly signPublicKey: Uint8Array
  readonly agreePublicKey: Uint8Array
  readonly signedPreKey: SignedPreKeyPublic
  /** Ausente quando as OTPKs do dono acabaram — o X3DH ainda funciona. */
  readonly oneTimePreKey?: OneTimePreKeyPublic
}

export function createSignedPreKey(identity: Identity, keyId: number, now: number): SignedPreKey {
  const pair = sodium().crypto_box_keypair()
  return {
    keyId,
    publicKey: pair.publicKey,
    privateKey: pair.privateKey,
    signature: identity.sign(pair.publicKey),
    createdAt: now,
  }
}

export function createOneTimePreKeys(firstKeyId: number, count: number): OneTimePreKey[] {
  const s = sodium()
  const out: OneTimePreKey[] = []
  for (let i = 0; i < count; i++) {
    const pair = s.crypto_box_keypair()
    out.push({ keyId: firstKeyId + i, publicKey: pair.publicKey, privateKey: pair.privateKey })
  }
  return out
}

export interface SerializedPreKeyStore {
  readonly v: 1
  readonly signed: {
    readonly keyId: number
    readonly publicKey: string
    readonly privateKey: string
    readonly signature: string
    readonly createdAt: number
  }
  readonly oneTime: readonly {
    readonly keyId: number
    readonly publicKey: string
    readonly privateKey: string
  }[]
  readonly nextOneTimeKeyId: number
}

/** Guarda local das pre-chaves. A parte privada nunca e publicada. */
export class PreKeyStore {
  private signed: SignedPreKey
  private readonly oneTime = new Map<number, OneTimePreKey>()
  private nextOneTimeKeyId: number

  private constructor(signed: SignedPreKey, oneTime: readonly OneTimePreKey[]) {
    this.signed = signed
    for (const k of oneTime) this.oneTime.set(k.keyId, k)
    this.nextOneTimeKeyId = oneTime.reduce((max, k) => Math.max(max, k.keyId + 1), 1)
  }

  static create(
    identity: Identity,
    now: number,
    oneTimeCount: number = DEFAULT_ONE_TIME_PREKEY_COUNT,
  ): PreKeyStore {
    return new PreKeyStore(
      createSignedPreKey(identity, 1, now),
      createOneTimePreKeys(1, oneTimeCount),
    )
  }

  get signedPreKey(): SignedPreKey {
    return this.signed
  }

  get availableOneTimeCount(): number {
    return this.oneTime.size
  }

  needsRotation(now: number): boolean {
    return now - this.signed.createdAt >= SIGNED_PREKEY_LIFETIME_SECONDS
  }

  /**
   * Gera a proxima Signed PreKey. A anterior e apagada da memoria: manter a
   * chave velha por conveniencia derrubaria o sigilo futuro que a rotacao
   * existe para dar.
   */
  rotateSignedPreKey(identity: Identity, now: number): SignedPreKey {
    const previous = this.signed
    this.signed = createSignedPreKey(identity, previous.keyId + 1, now)
    wipe(previous.privateKey)
    return this.signed
  }

  /** Consome uma OTPK. Uma vez usada, some — nunca e servida de novo. */
  takeOneTimePreKey(keyId: number): OneTimePreKey | undefined {
    const key = this.oneTime.get(keyId)
    if (key === undefined) return undefined
    this.oneTime.delete(keyId)
    return key
  }

  /** Lote novo para repor o estoque no servidor. */
  replenishOneTimePreKeys(count: number): OneTimePreKey[] {
    const batch = createOneTimePreKeys(this.nextOneTimeKeyId, count)
    for (const k of batch) this.oneTime.set(k.keyId, k)
    this.nextOneTimeKeyId += count
    return batch
  }

  /**
   * Estado para persistir no cofre. CONTEM MATERIAL SECRETO: as metades
   * privadas de toda pre-chave. Nunca vai para o servidor.
   *
   * Sem isto o app perderia as chaves privadas ao fechar, e ninguem mais
   * conseguiria abrir sessao com esta identidade — o X3DH do lado de quem
   * responde precisa exatamente destas metades.
   */
  serialize(): SerializedPreKeyStore {
    return {
      v: 1,
      signed: {
        keyId: this.signed.keyId,
        publicKey: toBase64Url(this.signed.publicKey),
        privateKey: toBase64Url(this.signed.privateKey),
        signature: toBase64Url(this.signed.signature),
        createdAt: this.signed.createdAt,
      },
      oneTime: [...this.oneTime.values()].map((k) => ({
        keyId: k.keyId,
        publicKey: toBase64Url(k.publicKey),
        privateKey: toBase64Url(k.privateKey),
      })),
      nextOneTimeKeyId: this.nextOneTimeKeyId,
    }
  }

  static deserialize(state: SerializedPreKeyStore, decode: (s: string) => Uint8Array): PreKeyStore {
    const store = new PreKeyStore(
      {
        keyId: state.signed.keyId,
        publicKey: decode(state.signed.publicKey),
        privateKey: decode(state.signed.privateKey),
        signature: decode(state.signed.signature),
        createdAt: state.signed.createdAt,
      },
      state.oneTime.map((k) => ({
        keyId: k.keyId,
        publicKey: decode(k.publicKey),
        privateKey: decode(k.privateKey),
      })),
    )
    // O contador nao pode ser recalculado a partir das chaves que sobraram:
    // se as ultimas ja foram consumidas, reemitir os mesmos keyId faria o
    // servidor casar uma OTPK nova com uma privada que nao existe mais.
    store.nextOneTimeKeyId = state.nextOneTimeKeyId
    return store
  }

  /** Somente parte publica — e isto que vai para o Worker. */
  publicBundle(identity: Identity, oneTimeKeyId?: number): PreKeyBundle {
    const otp = oneTimeKeyId === undefined ? undefined : this.oneTime.get(oneTimeKeyId)
    const base = {
      signPublicKey: identity.publicIdentity.signPublicKey,
      agreePublicKey: identity.publicIdentity.agreePublicKey,
      signedPreKey: {
        keyId: this.signed.keyId,
        publicKey: this.signed.publicKey,
        signature: this.signed.signature,
        createdAt: this.signed.createdAt,
      },
    }
    return otp === undefined
      ? base
      : { ...base, oneTimePreKey: { keyId: otp.keyId, publicKey: otp.publicKey } }
  }
}
